/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  OnInit,
  signal,
  SimpleChanges,
  WritableSignal,
} from '@angular/core';
import {
  ControlsOf,
  FormBuilder,
  FormControl,
  FormGroup,
} from '@ngneat/reactive-forms';
import { TranslocoService } from '@ngneat/transloco';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ColumnRanges, InputType, ITableColumn } from '@vinnova/ui';
import { formatISO } from 'date-fns';
import { filter, map, Observable, Subscription, tap } from 'rxjs';
import { Answer } from '../../../../core/models/answers/answer.model';
import { FormBudget } from '../../../../core/models/form-group-mapping.model';
import { ReadonlyState } from '../../../../core/models/form.model';
import { Question } from '../../../../core/models/question/question.model';
import { StructuredDataKey } from '../../../../core/models/question/structured-data-key.model';
import { Section } from '../../../../core/models/section.model';
import { RendererFormsQuery } from '../../../../core/queries/renderer-forms.query';
import { RendererSectionsQuery } from '../../../../core/queries/renderer-sections.query';
import { FormValidationService } from '../../../../core/services/form-validation.service';
import { QuestionValueChangeService } from '../../../../core/services/question-value-change.service';
import { getBudgetTableColumns } from '../utils/projektbidrag-budget.utils';
import { SkissBudgetKostnaderRequired } from '../validators/budget-kostnader-required.validator';

@UntilDestroy()
@Component({
  selector: 'lib-question-skiss-budget',
  templateUrl: './skiss-budget.component.html',
  styleUrls: ['./skiss-budget.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class SkissBudgetComponent implements OnInit, OnChanges {
  ROW = [{ id: '1', translationKey: 'question_budget_skiss_row_title' }];

  @Input() questionForm: FormGroup<ControlsOf<FormBudget>>;
  @Input() repeaterGroupId: string;
  @Input() formBuilder: boolean;
  @Input() question: Question;

  readOnly$: Observable<ReadonlyState>;
  projektTimeSection: Section;
  startDate: Date;
  endDate: Date;
  InputType = InputType;
  yearColumns: ITableColumn[];
  browseLarge: WritableSignal<ColumnRanges> = signal([]);
  browseMedium: WritableSignal<ColumnRanges> = signal([]);
  browseSmall: WritableSignal<ColumnRanges> = signal([]);
  beloppFormControl: FormControl<string>;

  activeLang$: Observable<string>;
  projektTidInvalid: boolean;
  checkValidation$: Observable<boolean>;
  valueChangeSubscription: Subscription;
  patchValueSubscription: Subscription;

  ReadonlyState = ReadonlyState;

  constructor(
    private questionValueChangeService: QuestionValueChangeService,
    private sectionQuery: RendererSectionsQuery,
    private formsQuery: RendererFormsQuery,
    private translocoService: TranslocoService,
    private cdr: ChangeDetectorRef,
    private fb: FormBuilder,
    private formValidationService: FormValidationService,
  ) {}

  ngOnInit(): void {
    this.activeLang$ = this.translocoService.langChanges$;
    this.readOnly$ = this.formsQuery.selectReadOnly();

    this.questionValueChangeService.patchInitialValue(
      this.question.id,
      this.repeaterGroupId,
      this.questionForm.get('value'),
      this.question,
    );

    this.projektTimeSection = this.sectionQuery.getSectionWithKeyQuestion(
      StructuredDataKey.Projekttid,
    );
    const projekttidQuestion =
      this.questionValueChangeService.getOrCreateKeyValue(
        StructuredDataKey.Projekttid,
      );
    if (projekttidQuestion) {
      projekttidQuestion
        .pipe(
          map((a: { answer: Answer; valid: boolean }) => {
            this.projektTidInvalid = !a?.valid;
            return a?.answer?.value && a.valid
              ? JSON.parse(a.answer.value)
              : undefined;
          }),
          tap((value) => {
            this.setYearColumns(value);
            this.createBudgetFormControls();
          }),
          filter(() => !!this.questionForm.value.value),
          tap(() => this.setValue()),
          untilDestroyed(this),
        )
        .subscribe();
    }
    this.checkValidation$ = this.formValidationService.showErrors$;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['repeaterGroupId']) {
      if (this.valueChangeSubscription) {
        this.valueChangeSubscription.unsubscribe();
      }
      if (this.patchValueSubscription) {
        this.valueChangeSubscription.unsubscribe();
      }

      this.valueChangeSubscription = this.questionValueChangeService
        .valueChangeListener(
          this.questionForm.get('value'),
          {
            questionId: this.question.id,
            repeaterGroupId: this.repeaterGroupId,
          },
          this.question,
        )
        .pipe(untilDestroyed(this))
        .subscribe();

      this.patchValueSubscription = this.questionValueChangeService
        .patchValueListener(
          this.question,
          this.repeaterGroupId,
          this.questionForm.get('value'),
        )
        .pipe(untilDestroyed(this))
        .subscribe(() => this.createBudgetFormControls());
    }
  }

  private setYearColumns(value) {
    this.startDate = value?.fromDate ? new Date(value.fromDate) : undefined;
    this.endDate = value?.toDate ? new Date(value.toDate) : undefined;
    const startYear = this.startDate?.getFullYear();
    const endYear = this.endDate?.getFullYear();
    const {
      browseSmall: browseSmall,
      browseMedium,
      browseLarge,
      yearColumns,
    } = getBudgetTableColumns(startYear, endYear);

    this.yearColumns = yearColumns;
    this.browseLarge.set(browseLarge);
    this.browseMedium.set(browseMedium);
    this.browseSmall.set(browseSmall);

    this.cdr.markForCheck();
  }

  createBudgetFormControls() {
    const parsedValue = this.questionForm.value.value
      ? JSON.parse(this.questionForm.value.value)
      : undefined;

    let beloppValue;
    if (parsedValue && this.startDate && this.endDate) {
      beloppValue = [];
      for (const arSumma of parsedValue.soktBidrag) {
        beloppValue.push({
          rowId: '1',
          columnId: (arSumma.ar - this.startDate.getFullYear()).toString(),
          value: arSumma.summa,
        });
      }
    }

    this.beloppFormControl = this.fb.control<string>(
      beloppValue ? JSON.stringify(beloppValue) : undefined,
    );
    this.questionForm.setControl('beloppFormControl', this.beloppFormControl);
    this.setValidation(this.beloppFormControl);
    this.cdr.markForCheck();
  }

  updateBudgetTables() {
    this.setValue();
  }

  private setValidation(control: FormControl<string>) {
    control.clearValidators();
    control.addValidators(SkissBudgetKostnaderRequired);
    control.updateValueAndValidity();
  }

  private setValue() {
    let jsonValue;
    if (this.startDate && this.endDate) {
      jsonValue = {} as any;
      jsonValue.startDatum = formatISO(this.startDate);
      jsonValue.slutDatum = formatISO(this.endDate);
      jsonValue.soktBidrag = [];
      const soktaBidrag = this.beloppFormControl.value
        ? JSON.parse(this.beloppFormControl.value)
        : [];
      for (const year of this.yearColumns) {
        const bidragYearJsonValue = {} as any;
        const bidrag =
          soktaBidrag.find((k) => k.columnId === year.id)?.value ?? 0;
        bidragYearJsonValue.ar = +year.title;
        bidragYearJsonValue.summa = bidrag;
        jsonValue.soktBidrag.push(bidragYearJsonValue);
      }

      this.questionForm
        .get('value')
        .patchValue(jsonValue ? JSON.stringify(jsonValue) : jsonValue);
      this.questionForm.markAsTouched();
      this.questionForm.get('value').markAsDirty();
    }
    this.formValidationService.triggerSectionInvalidCheck(this.question.id);
  }
}
